/**
 * A PUSKA MAGJA — tiszta függvények, React nélkül.
 *
 * A könyvelő GÉPELÉS KÖZBEN akarja látni, hogy a kontír kiadja-e a bruttót.
 * A szerver is kiszámolja (`allocated`), de csak mentés után — és a mentés
 * a fókusz elhagyásakor megy. A futó összeget ezért itt is elő kell
 * állítani, ÉS UGYANÚGY PONTOSAN, mint odaát: ha a kettő eltér, a szám
 * mentéskor megugrana, és a könyvelő nem tudná, melyiknek higgyen.
 */

import type { BookingProposalSheet, DraftRow, ProposalPin } from './types';

/**
 * AZ ÖSSZEG ALAKJA — a szerver `parseAmount`-jának tükre
 * (`booking-proposal.service.ts`): előjeles egész, legfeljebb két tizedes.
 *
 * ⚠️ MIÉRT NEM BAJ, HA ELCSÚSZIK. A drift ára itt kicsi és MEGNEVEZETT: a
 * szerver `400`-at ad, és a hibaüzenete kiírja az elutasított értéket. Ez
 * nem a 2.6/22 előellenőrzése, ahol a kurzor ugrálna — itt a könyvelő a
 * mezőben áll, és a mondat mellette jelenik meg. A tükör a KÉNYELEMÉRT van
 * (azonnali visszajelzés), nem a helyességért.
 */
const AMOUNT_SHAPE = /^-?\d{1,16}(\.\d{1,2})?$/;

export function isAmountShaped(value: string): boolean {
  return AMOUNT_SHAPE.test(value.trim());
}

/** Üres mező = nincs érték. A szerver ugyanezt teszi (`trimToNull`). */
function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A SZERVER ALAKJÁBÓL SZERKESZTHETŐ ALAK.
 *
 * A `key` a szerveroldali azonosító, amíg van — így egy mentés utáni
 * újratöltés NEM mountolja újra a sorokat, és a fókusz a helyén marad.
 */
export function toDraft(sheet: BookingProposalSheet): DraftRow[] {
  return sheet.rows.map((row) => ({
    key: row.id,
    id: row.id,
    accountCode: row.accountCode ?? '',
    amount: row.amount ?? '',
    note: row.note ?? '',
    // `?? null`: a jelölőt nem ismerő szerver-verzió (a backend külön
    // deployol) a mezőt el sem küldi, és az `undefined` „tűzöttnek" számítana.
    pin: row.pin ?? null,
  }));
}

/**
 * ÚJ, ÜRES SOR. A kulcs a hívótól jön, mert egy `Math.random()` itt
 * teszthetetlenné tenné a modult — és a kulcsnak csak a listán belül kell
 * egyedinek lennie.
 */
export function emptyRow(key: string): DraftRow {
  return { key, id: null, accountCode: '', amount: '', note: '', pin: null };
}

/**
 * KITÖLTÖTT, ha van benne szöveg — VAGY a papírhoz van tűzve. A szöveg
 * nélküli jelölő is a könyvelő jele („ezt nézd meg"), és ha nem számítana
 * kitöltöttnek, a letétele némán elveszne: a terv sosem küldené ki.
 */
export function isFilled(row: DraftRow): boolean {
  return (
    row.accountCode.trim().length > 0 ||
    row.amount.trim().length > 0 ||
    row.note.trim().length > 0 ||
    row.pin !== null
  );
}

/** A sor jegyzet-mezőjének azonosítója — ide visz a papír `N`-je és `E`-je. */
export function noteFieldId(key: string): string {
  return `booking-note-${key}`;
}

/**
 * A KIÜRÜLT SOROK KIESNEK — csak a záró üres sor marad (#68, Viktor 10-20).
 *
 * Aki az üres záró sorba ír, annak a horog új üres sort tesz alá; ha utána
 * kitörli a szövegét, két üres mező állt egymás alatt. A mentés a kiürült
 * sort úgyis törli (vagy ki sem küldi) — a felületnek is el kell engednie.
 *
 * ⚠️ CSAK A MEZŐ ELHAGYÁSAKOR, nem gépelés közben: ott a kurzor alól tűnne
 * el a mező, amikor az utolsó betűt kitörlöd, hogy újat írj. Ezért a
 * `keepKey` sora — az, amelyikben a fókusz MOST áll — marad: az `Enter`
 * is ment, de a mezőben hagyja a kurzort, és a kiejtett mező alól a
 * fókusz a semmibe esne (ahonnan a következő `Enter` már lefűzne).
 */
export function dropEmptyRows(drafts: DraftRow[], keepKey: string | null = null): DraftRow[] {
  return drafts.filter(
    (row, index) => isFilled(row) || index === drafts.length - 1 || row.key === keepKey
  );
}

const FIELDS = ['accountCode', 'amount', 'note'] as const;
type Field = (typeof FIELDS)[number];

/**
 * Egy sor értékei, ahogy a szerver várja. A `PATCH`-nél a HIÁNYZÓ mező
 * érintetlen marad; a `pin: null` leveszi a jelölőt.
 */
export type RowValues = Partial<Record<Field, string | null>> & { pin?: ProposalPin | null };

/** Egy írás a szerver felé — a `POST` a vázlatsor kulcsát viszi, hogy a válasz azonosítója visszataláljon. */
export type Write =
  | { kind: 'delete'; rowId: string }
  | { kind: 'patch'; rowId: string; values: RowValues }
  | { kind: 'post'; key: string; values: RowValues };

/**
 * MIT KELL KIÍRNI — a szerkesztett sorok és az utolsó ismert szerverállapot
 * KÜLÖNBSÉGE, soronként.
 *
 * Zászló helyett összehasonlítás: a zászlót be kell állítani, és pont az az
 * egy hely marad ki, ahol számítana. Az összehasonlítás nem tud elfelejtődni.
 *
 *   • a szerveren álló, de a vázlatból eltűnt (vagy kiürített) sor → `delete`;
 *   • a megváltozott mezők → `patch`, CSAK azokkal: egy egész sort visszaírva
 *     a máshol közben átírt mezőt is felülírnánk;
 *   • az azonosító nélküli, kitöltött sor → `post`.
 *
 * A SORREND: törlés, módosítás, új sor. A törlés előre megy, mert helyet
 * szabadít a sorkorlát alatt; az új sorok a vázlat sorrendjében jönnek,
 * mert a szerver a végére fűzi őket.
 *
 * A TELJESEN ÜRES SOR NEM MEGY KI. A felület mindig hagy egy üres sort a
 * lista végén, hogy a könyvelőnek ne kelljen „új sor" gombot keresnie.
 */
export function planWrites(drafts: DraftRow[], sheet: BookingProposalSheet | null): Write[] {
  const stored = new Map((sheet?.rows ?? []).map((row) => [row.id, row]));
  const kept = new Set<string>();
  const patches: Write[] = [];
  const posts: Write[] = [];

  for (const draft of drafts) {
    if (!isFilled(draft)) {
      continue;
    }

    const current = draft.id === null ? undefined : stored.get(draft.id);
    if (current === undefined) {
      // A TŰZETLEN ÚJ SOR `pin` NÉLKÜL megy: a jelölőt nem ismerő szerver-
      // verzió (külön deployol) így is elfogadja.
      const { pin, ...text } = valuesOf(draft);
      posts.push({ kind: 'post', key: draft.key, values: pin === null ? text : { ...text, pin } });
      continue;
    }

    kept.add(current.id);
    const changed = changesOf(draft, current);
    if (Object.keys(changed).length > 0) {
      patches.push({ kind: 'patch', rowId: current.id, values: changed });
    }
  }

  const deletes: Write[] = [...stored.keys()]
    .filter((id) => !kept.has(id))
    .map((rowId) => ({ kind: 'delete', rowId }));

  return [...deletes, ...patches, ...posts];
}

/**
 * AZ ÖSSZEG ÉRTÉK SZERINT EGYEZIK, nem betűre. A szerver `toFixed(2)`-vel
 * válaszol: a gépelt `679000` `679000.00`-ként jön vissza. Szövegként
 * hasonlítva minden további fókuszvesztés újra elküldené — és felülírná,
 * amit közben más írt abba a mezőbe.
 */
function sameValue(field: Field, draft: string | null, stored: string | null): boolean {
  if (field === 'amount' && draft !== null && stored !== null) {
    if (isAmountShaped(draft) && isAmountShaped(stored)) {
      return toCents(draft) === toCents(stored);
    }
  }
  return draft === stored;
}

/** A mentett sorhoz képest MEGVÁLTOZOTT mezők — csak ezek mennek a `PATCH`-ben. */
function changesOf(draft: DraftRow, current: BookingProposalSheet['rows'][number]): RowValues {
  const values = valuesOf(draft);
  const changed: RowValues = {};
  for (const field of FIELDS) {
    if (!sameValue(field, values[field], current[field])) {
      changed[field] = values[field];
    }
  }
  // `?? null`: lásd `toDraft` — a régi szerver során nincs `pin`, és nem
  // kaphat olyan `PATCH`-et, ami a jelölőt nem ismerő végpontra `pin`-t küld.
  if (!samePin(values.pin, current.pin ?? null)) {
    changed.pin = values.pin;
  }
  return changed;
}

function samePin(draft: ProposalPin | null, stored: ProposalPin | null): boolean {
  if (draft === null || stored === null) {
    return draft === stored;
  }
  // NEM PONTOS EGYEZÉS: a szerveren átment koordináta az utolsó jegyben
  // eltérhet (mérve 10-19: kiment `0.21794258373205744`, visszajött
  // `0.2179425837320574`), és pontos egyezéssel minden mentés után egy
  // fölösleges `PATCH` menne ki ugyanarra a helyre.
  return (
    draft.page === stored.page &&
    Math.abs(draft.x - stored.x) < FLOAT_SLACK &&
    Math.abs(draft.y - stored.y) < FLOAT_SLACK
  );
}

/** Üres mező = nincs érték: `null`-ként megy ki, nem üres szövegként. */
function valuesOf(draft: DraftRow): Record<Field, string | null> & { pin: ProposalPin | null } {
  return {
    accountCode: blankToNull(draft.accountCode),
    amount: blankToNull(draft.amount),
    note: blankToNull(draft.note),
    pin: draft.pin,
  };
}

/**
 * EGY SORNAK SZÁMÍT, ami a lap magasságának ennyi részén belül van — a
 * szerver `PIN_ROW_TOLERANCE`-ének tükre (Viktor, 10-19).
 */
export const PIN_ROW_TOLERANCE = 0.01;

/**
 * A LEBEGŐPONTOS RÁHAGYÁS a sáv szélén. `0.31 - 0.3` nem `0.01`, hanem
 * `0.010000000000000009` — ráhagyás nélkül a pontosan 1%-ra lévő két jelölő
 * a lap egyik részén egy sor volna, a másikon kettő (mérve a böngészőben,
 * 10-19: nyíllal 1%-ot lépve).
 */
const FLOAT_SLACK = 1e-9;

/**
 * A JELÖLŐK SZÁMA, vázlatkulcsonként — a szerver `pinNumbers`-ének tükre:
 * fentről lefelé, a lap 1%-án belül egy sor, azon belül balról jobbra.
 *
 * MIÉRT ITT IS. A jelölő a letétel PILLANATÁBAN kap számot, nem a mentés
 * válaszára várva — a könyvelő a „4"-est látja letenni, és ahhoz írja a
 * jegyzetet. A szerver száma csak egy körrel később jönne.
 *
 * ⚠️ EGY HELYEN TÉR EL, és megnevezve: pontosan azonos sávban ÉS azonos X-en
 * a szerver a létrehozás idejével dönt, itt a lista sorrendjével. A sorok
 * létrehozási sorrendben állnak, tehát ez a gyakorlatban ugyanaz; kézzel
 * letett jelölőnél a pontos egyezés amúgy sem fordul elő.
 */
export function pinNumbers(drafts: DraftRow[]): Map<string, number> {
  const pinned = drafts.flatMap((row, order) =>
    row.pin === null ? [] : [{ key: row.key, pin: row.pin, order, band: 0 }]
  );

  pinned.sort((a, b) => a.pin.page - b.pin.page || a.pin.y - b.pin.y);

  // A SÁV A LEGFELSŐ TAGJÁHOZ MÉRŐDIK, és nem lép át a következő oldalra —
  // lásd a szerver indoklását.
  let band = 0;
  let bandPage = Number.NaN;
  let bandTop = Number.NaN;
  for (const item of pinned) {
    if (item.pin.page !== bandPage || item.pin.y - bandTop > PIN_ROW_TOLERANCE + FLOAT_SLACK) {
      band += 1;
      bandPage = item.pin.page;
      bandTop = item.pin.y;
    }
    item.band = band;
  }

  pinned.sort((a, b) => a.band - b.band || a.pin.x - b.pin.x || a.order - b.order);

  return new Map(pinned.map((item, index) => [item.key, index + 1]));
}

/**
 * A FUTÓ ÖSSZEG — FILLÉRBEN, `BigInt`-tel.
 *
 * NEM `number`. A `Decimal(18, 2)` tizenhat egész jegyet enged; fillérre
 * váltva az `2^53` fölé megy, és ott a `double` már egészeket sem tud
 * pontosan. Mérve a szerver oldalán: `1234567890123456.78 + 0.01`
 * lebegőpontosan `…56.80`, pontosan `…56.79`. A `BigInt` egzakt, és nem kell
 * hozzá könyvtár.
 *
 * `null`, HA BÁRMELYIK SOR ALAKJA ROSSZ. Egy félig értelmezett összeg
 * hazudna: a könyvelő azt hinné, a mérce stimmel, holott egy sort a
 * számítás kihagyott.
 */
export function sumAmounts(drafts: DraftRow[]): string | null {
  let cents = 0n;

  for (const row of drafts) {
    const value = row.amount.trim();
    if (value.length === 0) {
      continue;
    }
    if (!isAmountShaped(value)) {
      return null;
    }
    cents += toCents(value);
  }

  return fromCents(cents);
}

function toCents(value: string): bigint {
  const negative = value.startsWith('-');
  const [whole, fraction = ''] = value.replace(/^-/, '').split('.');
  const padded = `${fraction}00`.slice(0, 2);
  const total = BigInt(whole) * 100n + BigInt(padded);

  return negative ? -total : total;
}

function fromCents(cents: bigint): string {
  const negative = cents < 0n;
  const absolute = negative ? -cents : cents;
  const whole = absolute / 100n;
  const fraction = (absolute % 100n).toString().padStart(2, '0');

  return `${negative ? '-' : ''}${whole}.${fraction}`;
}
